/**
 * @fileoverview PaymentDialog - Comprehensive Payment Management Interface
 *
 * This component provides a full-featured payment creation and editing interface
 * for staff users to manage environmental compliance payments. It handles both
 * regular staff-recorded payments and automated Lob check payments.
 *
 * ## Key Features:
 * - **Dual Payment Types**: Regular payments (staff-recorded) and Lob payments (automated checks)
 * - **Document Management**: Invoice and receipt upload/management with validation
 * - **Multi-Entity Associations**: Link payments to reports, facilities, and activities
 * - **Reconciliation Workflow**: Finance team reconciliation status management
 * - **Unsaved Changes Protection**: Automatic cleanup of orphaned documents
 * - **Payment Comments**: Threaded comment system for payment discussions
 * - **Tenant Management**: Multi-organization payment support for staff users
 *
 * ## Business Context:
 *
 * ### Payment Types:
 * - **Regular Payments**: Staff manually record payment details (amount, method, date)
 * - **Lob Payments**: Automated check printing/mailing via Lob service with strict validation
 *
 * ### Payment Lifecycle:
 * 1. **Creation**: Staff creates payment with required documents and associations
 * 2. **Processing**: Lob payments go through job queue, regular payments are immediate
 * 3. **Reconciliation**: Finance team marks payments as reconciled/problem/unreconciled
 * 4. **Audit Trail**: Comments and reconciliation history track all changes
 *
 * ### Document Management:
 * - **Invoices**: Required for all payments (exactly one for Lob payments, at least one for regular payments)
 * - **Receipts**: Optional for all payments, can be added/changed after creation
 * - **Cleanup**: Orphaned documents are automatically deleted if payment creation is cancelled
 *
 * ### Associations:
 * - **Reports**: Primary and additional associated reports for compliance tracking
 * - **Facilities**: Facilities related to the payment for proper allocation
 * - **Activities**: Activity context for payment categorization
 *
 * ## Technical Architecture:
 *
 * ### State Management:
 * - **React Hook Form**: Form state, validation, and submission
 * - **Apollo Client**: GraphQL data fetching and mutations
 * - **Local State**: UI state for modals, document uploads, reconciliation
 *
 * ### Validation:
 * - **Yup Schema**: Client-side form validation with dynamic rules
 * - **Business Rules**: Lob-specific validation, document requirements
 * - **Server Validation**: GraphQL input validation on submission
 *
 * ### Layout:
 * - **Two-Panel Design**: Left panel (form), right panel (associations/reconciliation)
 * - **Responsive**: Adapts to different screen sizes and content
 * - **Modal Dialogs**: Document upload, reconciliation, tenant selection
 *
 * ## Integration Points:
 * - **Lob API**: Automated check printing and mailing service
 * - **Document Service**: File upload, storage, and retrieval
 * - **Reconciliation Service**: Finance team workflow management
 * - **Comment System**: Threaded discussions on payments
 * - **Tenant Management**: Multi-organization support
 *
 * ## Props Interface:
 * @param open - Controls dialog visibility
 * @param onClose - Callback when dialog is closed
 * @param tenantId - Organization ID for payment (optional for staff users)
 * @param onCompleted - Callback after successful payment creation/update
 * @param facilityId - Facility context for payment
 * @param reportId - Primary report association
 * @param activityId - Activity context for categorization
 * @param paymentId - Payment ID for editing (undefined for new payments)
 * @param associatedReportIds - Additional report associations
 * @param onReconcilePayment - Callback for reconciliation actions
 * @param defaultPayeeType - Default payee type to pre-select for new payments
 */

import { yupResolver } from "@hookform/resolvers/yup";
import MonetizationOn from "@mui/icons-material/MonetizationOn";
import Info from "@mui/icons-material/Info";
import ContentCopy from "@mui/icons-material/ContentCopy";
import OpenInNew from "@mui/icons-material/OpenInNew";
import Cancel from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  Tooltip,
  Chip,
} from "@mui/material";
import { ConfirmDialog } from "components/ConfirmDialog";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { RadioGroupField } from "components/Forms/RadioGroupField";
import {
  DocumentType,
  PaymentMethod,
  PaymentStatus,
  Payee,
} from "generated-graphql/graphql";
import {
  useMakeLobPayment,
  usePayment,
  useUpsertPayment,
} from "hooks/usePayment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import { useTenant } from "hooks/useTenant";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "generated-graphql";
import { useRegisterCommands } from "components/CommandPaletteRegistry";
import { commandCopy } from "components/commandPalette/commandBuilders";
import { AssociatedEntities } from "./AssociatedEntities";
import { deriveAssociationCategoryState } from "./lib/associationUtils";
import type { AssociationCategory } from "./lib/associationUtils";
import { PaymentType } from "./lib/types";
import type { PayeeFormInputs } from "./lib/types";
import { calculateAmountPerAssociation } from "./lib/utils";
import { createValidationSchema } from "./lib/validation";
import { useDebouncedCallback } from "use-debounce";
import { v4 } from "uuid";
import { TenantPickerDialog } from "components/TenantPickerDialog";
import { ReconcilePaymentDialog } from "components/ReconcilePaymentDialog";
import {
  buildDefaultAssociationsForNewPayment,
  calculatePaymentDefaults,
  determinePaymentEditability,
  generatePaymentUrl,
  isLobPayment,
  preparePaymentSubmissionData,
  getPaymentStatusColor,
  getJobStatusColor,
  checkForUnsavedDocumentChanges,
  handleDocumentCleanup,
  getInvoiceDocumentId,
  prepareLobCheckData,
  checkForUnsavedAssociationsChanges,
} from "./lib";
import { PaymentDialogSkeleton } from "./PaymentDialogSkeleton";
import { PayeeSection } from "./sections/PayeeSection";
import { PaymentSection } from "./sections/PaymentSection";
import { InvoicesAttachmentsSection } from "./sections/InvoicesAttachmentsSection";
import { ReconciliationSection } from "./sections/ReconciliationSection";
import { CommentsSection } from "./sections/CommentsSection";

const GET_TENANT_NAME = gql(`
  query GetTenantName($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      name
    }
  }
`);

const CANCEL_LOB_CHECK = gql(`
  mutation CancelLobCheck($id: ID!) {
    cancelLobCheck(id: $id) {
      id
      status
    }
  }
`);

const associationCategoryLabels: Record<AssociationCategory, string> = {
  report: "reports",
  facility: "facilities",
  submission: "submissions",
};

const sectionTypeToAssociationCategory = {
  Report: "report",
  Facility: "facility",
  Submission: "submission",
} as const;

const PaymentDialog = ({
  open,
  onClose,
  tenantId: propTenantId,
  onCompleted,
  facilityId,
  associatedFacilityIds,
  reportId,
  activityId,
  paymentId,
  associatedReportIds,
  onReconcilePayment,
  associatedSubmissionIds,
  defaultPayeeType,
}: {
  open: boolean;
  onClose: () => void;
  tenantId?: string;
  onCompleted?: () => void;
  facilityId?: string;
  associatedFacilityIds?: string[];
  reportId?: string;
  activityId?: string;
  paymentId?: string;
  associatedReportIds?: string[];
  onReconcilePayment?: (paymentId: string) => void;
  associatedSubmissionIds?: string[];
  defaultPayeeType?: Payee;
}) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const { tenant: currentTenant } = useTenant();
  const { user: currentUser, isStaff } = useCurrentUser();
  const [showReconcileDialog, setShowReconcileDialog] = useState(false);

  const {
    payment: existingPayment,
    loading: loadingPayment,
    refetch: refetchPayment,
  } = usePayment(paymentId);

  const [showTenantPicker, setShowTenantPicker] = useState(false);

  const [upsert, { loading: recordingPayment }] = useUpsertPayment(reportId);

  const [sendLobCheck, { loading: sendingLobCheck }] =
    useMakeLobPayment(reportId);

  const [cancelLobCheckMutation, { loading: cancellingLobCheck }] = useMutation(
    CANCEL_LOB_CHECK,
    {
      onCompleted: () => {
        alerts.success("Check cancelled successfully");
        refetchPayment();
      },
      onError: (error) => {
        // Dont output the error to the user, but if debugging you can do so here
        alerts.error("Failed to cancel check");
      },
    }
  );

  const [addEditDocumentDialogState, setAddEditDocumentDialogState] =
    useState<AddEditDocumentDialogState>(defaultAddEditDocumentDialogState);

  // Unsaved changes tracking
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [cleaningUpDocuments, setCleaningUpDocuments] = useState(false);
  const [showCancelCheckConfirm, setShowCancelCheckConfirm] = useState(false);

  // Stabilize array props to prevent unnecessary re-renders when parent passes new array references.
  // These are only used for initial default values, so we capture them once on mount.
  const stableAssociatedReportIds = useRef(associatedReportIds).current;
  const stableAssociatedFacilityIds = useRef(associatedFacilityIds).current;
  const stableAssociatedSubmissionIds = useRef(associatedSubmissionIds).current;

  // Calculate default values based on current state
  const defaultValues = useMemo(() => {
    const tenantId =
      existingPayment?.tenantId ?? propTenantId ?? currentTenant?.id ?? "";

    const defaultAssociationsForNewPayment =
      buildDefaultAssociationsForNewPayment(
        existingPayment,
        reportId,
        stableAssociatedReportIds,
        stableAssociatedSubmissionIds,
        stableAssociatedFacilityIds
      );

    return calculatePaymentDefaults(
      existingPayment,
      reportId,
      stableAssociatedReportIds,
      activityId,
      tenantId,
      defaultAssociationsForNewPayment,
      defaultPayeeType
    );
  }, [
    existingPayment,
    propTenantId,
    currentTenant?.id,
    reportId,
    stableAssociatedReportIds,
    stableAssociatedFacilityIds,
    stableAssociatedSubmissionIds,
    activityId,
    defaultPayeeType,
  ]);

  const form = useForm<PayeeFormInputs>({
    resolver: yupResolver(
      createValidationSchema(paymentId, existingPayment)
    ) as any,
    defaultValues,
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isDirty },
    reset,
  } = form;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  // --- Association validation on edit ---
  // Track whether the form has been initialized (to skip validation on load/reset)
  const associationsInitializedRef = useRef(false);

  // Reset the ref when defaultValues change (form re-init for new/different payment)
  useEffect(() => {
    associationsInitializedRef.current = false;
    const id = setTimeout(() => {
      associationsInitializedRef.current = true;
    }, 0);
    return () => clearTimeout(id);
  }, [defaultValues]);

  // Debounced trigger to avoid excessive validation on rapid edits
  const debouncedTriggerAssociationValidation = useDebouncedCallback(() => {
    trigger(["associations", "amount"]);
  }, 300);

  const {
    append: appendInvoice,
    fields,
    remove,
  } = useFieldArray({
    control,
    name: "paymentInvoices",
  });

  const tenantId = watch("tenantId") ?? "";
  const associations = watch("associations");

  // Trigger validation when associations change after initial load
  useEffect(() => {
    if (associationsInitializedRef.current) {
      debouncedTriggerAssociationValidation();
    }
  }, [associations, debouncedTriggerAssociationValidation]);

  // Track unsaved changes - for existing payments
  const receiptDocument = watch("receiptDocument");

  // Fetch tenant name if we have an ID but no name
  const { data: tenantData } = useQuery(GET_TENANT_NAME, {
    variables: { tenantId },
    skip: loadingPayment || !!existingPayment?.tenantId || !tenantId,
  });

  const tenantName =
    existingPayment?.tenant?.name ?? tenantData?.tenant.name ?? "";

  const associationState = useMemo(
    () => deriveAssociationCategoryState(associations),
    [associations]
  );

  const isAssociationTypeLocked = useCallback(
    (type: "Report" | "Facility" | "Submission") => {
      const associationCategory = sectionTypeToAssociationCategory[type];

      if (associationState.kind === "mixed") {
        return true;
      }

      if (associationState.kind !== "single") {
        return false;
      }

      return associationState.category !== associationCategory;
    },
    [associationState]
  );

  const getAssociationLockReason = useCallback(
    (type: "Report" | "Facility" | "Submission") => {
      if (associationState.kind === "mixed") {
        return "This payment has mixed association types. Remove associations until only one type remains.";
      }

      if (associationState.kind !== "single") {
        return undefined;
      }

      const associationCategory = sectionTypeToAssociationCategory[type];

      if (associationState.category === associationCategory) {
        return undefined;
      }

      const activeAssociationTypeLabel =
        associationCategoryLabels[associationState.category];

      return `This payment is already associated with ${activeAssociationTypeLabel}. Payments can only be associated with one type at a time.`;
    },
    [associationState]
  );

  const hasUnsavedReceiptChanges = useMemo(() => {
    const result = checkForUnsavedDocumentChanges(
      paymentId,
      fields,
      receiptDocument,
      existingPayment?.document
    );

    return result;
  }, [paymentId, fields, receiptDocument, existingPayment?.document]);

  const hasUnsavedAssociationsChanges = useMemo(() => {
    const result = checkForUnsavedAssociationsChanges(
      paymentId,
      existingPayment,
      associations
    );

    return result;
  }, [existingPayment, associations, paymentId]);

  const noUnsavedChanges = useMemo(() => {
    return (
      !isDirty && !hasUnsavedReceiptChanges && !hasUnsavedAssociationsChanges
    );
  }, [isDirty, hasUnsavedReceiptChanges, hasUnsavedAssociationsChanges]);

  // Document deletion for pending documents
  const [deleteDocument] = useDeleteDocumentMutation({
    onCompleted: () => {
      alerts.success("Document deleted successfully");
      // Only refetch if there are no unsaved changes AND we have an existing payment
      if (noUnsavedChanges && paymentId) {
        refetchPayment();
      }
    },
    onError: (error) => {
      alerts.error("Failed to delete document", error);
    },
  });

  const {
    hasLobJob,
    hasRequeueableLobJob,
    hasSucceededLobJob,
    isExistingLobPayment,
    notEditable,
    payeeNotEditable,
    paymentDetailsNotEditable,
    metadataEditable,
  } = useMemo(() => {
    const editability = determinePaymentEditability(paymentId, existingPayment);

    return editability;
  }, [existingPayment, paymentId]);

  const isLob = isLobPayment(watch("type"), existingPayment, paymentId);

  const isPaid = existingPayment?.status === PaymentStatus.Paid;

  useEffect(() => {
    if (isLob) {
      setValue("paymentMethod", PaymentMethod.Check);
    }
    setValue(
      "type",
      isLob ? PaymentType.LOB_CHECK : PaymentType.RECORD_PAYMENT
    );
  }, [isLob, setValue]);

  const amount = watch("amount");
  const transactionFee = watch("transactionFee");
  const { replace: replaceAssociations } = useFieldArray({
    control,
    name: "associations",
  });

  const setReportAmounts = useCallback(() => {
    const associationsSum = associations.reduce(
      (partialSum, a) => partialSum + a.amount,
      0
    );

    if (
      associations.length &&
      (!associationsSum || associations.length === 1)
    ) {
      const amountsPerAssociation = calculateAmountPerAssociation(
        amount,
        associations.length
      );
      replaceAssociations(
        associations.map((r, idx) => ({
          ...r,
          amount: amountsPerAssociation[idx],
        }))
      );
    }
  }, [amount, associations, replaceAssociations]);

  // onSubmit callback - defined after all dependencies are available
  const onSubmit = useCallback(
    async (data: PayeeFormInputs) => {
      try {
        if (data.type === PaymentType.LOB_CHECK && !paymentId) {
          const invoiceDocumentId = getInvoiceDocumentId(data.paymentInvoices);

          // I would not expect this to ever happen until we add the ability to
          // upload multiple payment documents, but lets be safe
          if (!invoiceDocumentId) {
            alerts.error(
              "You must provide only a single invoice for a Lob payment."
            );
            return;
          }

          await sendLobCheck({
            variables: {
              input: prepareLobCheckData(data, invoiceDocumentId, tenantId),
            },
          });
        } else if (data.type === PaymentType.RECORD_PAYMENT || paymentId) {
          const submissionData = preparePaymentSubmissionData(
            data,
            paymentId,
            existingPayment,
            tenantId
          );

          await upsert({
            variables: {
              input: submissionData,
              requeueJob: data.requeueJob,
            },
          });
        }

        if (onCompleted) {
          await onCompleted();
        }

        onClose();
      } catch (error) {
        console.error(error);
        alerts.error("There was an error submitting the payment", error);
      }
    },
    [
      paymentId,
      alerts,
      sendLobCheck,
      tenantId,
      upsert,
      existingPayment,
      onCompleted,
      onClose,
    ]
  );

  // Cleanup uploaded documents when closing without saving
  const cleanupPendingDocuments = useCallback(async () => {
    await handleDocumentCleanup(
      paymentId,
      fields,
      receiptDocument,
      existingPayment?.document,
      deleteDocument
    );
  }, [paymentId, fields, receiptDocument, deleteDocument, existingPayment]);

  // Enhanced close handler with unsaved changes protection
  const handleClose = useCallback(() => {
    // The dialog is specific to receipt changes, so we only need to check for that
    if (hasUnsavedReceiptChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  }, [hasUnsavedReceiptChanges, onClose]);

  // Handle confirmed close with cleanup
  const handleConfirmedClose = useCallback(async () => {
    try {
      setCleaningUpDocuments(true);

      // Clean up pending documents before closing
      await cleanupPendingDocuments();

      setShowConfirmClose(false);
      onClose();
    } catch (error) {
      // Even if cleanup fails, allow the dialog to close
      setShowConfirmClose(false);
      onClose();
    } finally {
      setCleaningUpDocuments(false);
    }
  }, [cleanupPendingDocuments, onClose]);

  // Handler for cancelling Lob check
  const handleCancelLobCheck = useCallback(async () => {
    if (!paymentId) return;

    try {
      await cancelLobCheckMutation({
        variables: { id: paymentId },
      });
      setShowCancelCheckConfirm(false);
    } catch (error) {
      // Error handling is done in useMutation onError callback
      setShowCancelCheckConfirm(false);
    }
  }, [paymentId, cancelLobCheckMutation]);

  // Determine if payment can be cancelled
  const canCancelLobCheck = useMemo(() => {
    return Boolean(
      isStaff &&
        existingPayment?.lobMail?.id &&
        existingPayment?.status !== PaymentStatus.Cancelled &&
        existingPayment?.status !== PaymentStatus.Void
    );
  }, [isStaff, existingPayment]);

  const copyToClipboardWithAlert = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
      alerts.success("Copied to clipboard");
    },
    [alerts]
  );

  const paymentCopyCommands = useMemo(() => {
    if (!open || !paymentId || !existingPayment) return [];
    const commands = [];
    commands.push(
      commandCopy({
        id: "payment-dialog-copy-payment-id",
        idKind: "payment ID",
        value: paymentId,
        copy: copyToClipboardWithAlert,
        signature: `payment-id:${paymentId}`,
        keywords: ["payment", "id", "copy"],
      })
    );
    if (existingPayment.document?.id) {
      commands.push(
        commandCopy({
          id: "payment-dialog-copy-receipt-id",
          idKind: "receipt ID",
          value: existingPayment.document.id,
          copy: copyToClipboardWithAlert,
          signature: `payment-receipt:${existingPayment.document.id}`,
          keywords: ["payment", "receipt", "document", "id", "copy"],
        })
      );
    }
    existingPayment.paymentInvoices?.forEach((pi) => {
      const docId = pi.invoiceDocument?.id;
      if (docId) {
        commands.push(
          commandCopy({
            id: `payment-dialog-copy-invoice-id-${docId}`,
            idKind: "invoice ID",
            value: docId,
            copy: copyToClipboardWithAlert,
            signature: `payment-invoice:${docId}`,
            keywords: ["payment", "invoice", "document", "id", "copy"],
          })
        );
      }
    });
    if (existingPayment.lobMail?.id) {
      commands.push(
        commandCopy({
          id: "payment-dialog-copy-check-id",
          idKind: "check ID",
          value: existingPayment.lobMail.id,
          copy: copyToClipboardWithAlert,
          signature: `payment-check:${existingPayment.lobMail.id}`,
          keywords: ["payment", "check", "lob", "id", "copy"],
        })
      );
    }
    return commands;
  }, [open, paymentId, existingPayment, copyToClipboardWithAlert]);

  useRegisterCommands(
    paymentCopyCommands,
    [paymentCopyCommands],
    "payment-dialog"
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      title={
        paymentId && existingPayment ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}
          >
            <Typography variant="h6">Payment Details</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Open Job Button - only if job exists */}
              {existingPayment?.job?.id && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() => {
                    const jobUrl = `${window.location.origin}/staff/jobs/${existingPayment.job?.id}`;
                    window.open(jobUrl, "_blank");
                  }}
                  endIcon={
                    <Chip
                      label={existingPayment.job.status}
                      color={getJobStatusColor(existingPayment.job.status)}
                      variant="filled"
                      size="small"
                      sx={{
                        height: "20px",
                        fontSize: "0.65rem",
                        pointerEvents: "none", // Disable pointer events to prevent interference with button click
                        "& .MuiChip-label": {
                          padding: "0 6px",
                          fontSize: "0.65rem",
                          fontWeight: 500,
                        },
                      }}
                    />
                  }
                  sx={{ flexShrink: 0 }}
                >
                  Open Job
                </Button>
              )}

              {/* Copy Payment Link Button */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => {
                  const paymentUrl = generatePaymentUrl(paymentId, reportId);
                  navigator.clipboard.writeText(paymentUrl);
                  alerts.success("Payment link copied to clipboard");
                }}
                endIcon={
                  <Chip
                    label={existingPayment.status}
                    color={getPaymentStatusColor(existingPayment.status)}
                    variant="filled"
                    size="small"
                    sx={{
                      height: "20px",
                      fontSize: "0.65rem",
                      pointerEvents: "none", // Disable pointer events to prevent interference with button click
                      "& .MuiChip-label": {
                        padding: "0 6px",
                        fontSize: "0.65rem",
                        fontWeight: 500,
                      },
                    }}
                  />
                }
                sx={{ flexShrink: 0 }}
              >
                Copy Payment Link
              </Button>

              {/* Cancel Check Button - only for staff with Lob checks */}
              {canCancelLobCheck && (
                <Tooltip
                  title="Cancel this Lob check if it's still within the cancellation window"
                  arrow
                >
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => setShowCancelCheckConfirm(true)}
                    disabled={cancellingLobCheck}
                    sx={{ flexShrink: 0 }}
                  >
                    {cancellingLobCheck ? "Cancelling..." : "Cancel Check"}
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ) : (
          "Create Payment"
        )
      }
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "90vh",
          height: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      primaryActionButtonProps={{
        disabled: (() => {
          const conditions = {
            loadingPayment,
            pendingStatus: existingPayment?.status === PaymentStatus.Pending,
            noTenantForNewPayment: !paymentId && !tenantId,
            notEditable,
            noUnsavedChanges,
          };

          const isDisabled = Object.values(conditions).some(Boolean);

          return isDisabled;
        })(),
        loading: recordingPayment || sendingLobCheck,
        saveText: paymentId ? "Save Payment" : "Submit Payment",
        startIcon: isPaid ? undefined : <MonetizationOn />,
        onClick: handleSubmit(onSubmit),
      }}
      additionalPrimaryActionElements={[
        <>{errors.status && <ErrorDisplay error={errors.status} />}</>,
        <>
          {!paymentId && !tenantId && (
            <Tooltip
              title="Select an organization before submitting payment"
              arrow
              placement="top"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  cursor: "help",
                }}
              >
                <Info fontSize="small" />
                Organization required
              </Typography>
            </Tooltip>
          )}
        </>,
      ]}
    >
      <Box sx={{ flex: 1, overflow: "auto", p: 3, pt: 1 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Hidden field to track tenantId for validation */}
          <Controller
            name="tenantId"
            control={control}
            render={({ field }) => (
              <input type="hidden" {...field} value={tenantId} />
            )}
          />
          {!!paymentId && loadingPayment ? (
            <PaymentDialogSkeleton />
          ) : (
            <Stack gap={1}>
              {!paymentId && (
                <Box>
                  <RadioGroupField
                    name="type"
                    control={control}
                    label="Do you need to make or record a payment"
                    radioOptions={[
                      {
                        value: PaymentType.LOB_CHECK,
                        label: "Send a check with Lob",
                      },
                      {
                        value: PaymentType.RECORD_PAYMENT,
                        label: "Record a payment made elsewhere",
                      },
                    ]}
                  />
                </Box>
              )}
              {isExistingLobPayment && (
                <Typography variant="subtitle2">
                  This payment was sent to Lob. Editing the payment will NOT
                  send another check to Lob. Take caution editing this payment.
                </Typography>
              )}
              {hasLobJob && (
                <Box>
                  {!hasRequeueableLobJob && !hasSucceededLobJob && (
                    <Typography variant="subtitle2">
                      This payment was sent to Lob and is currently in progress.
                      You cannot edit this payment until the Lob job is
                      complete.
                    </Typography>
                  )}
                  {hasSucceededLobJob && (
                    <Typography variant="subtitle2" color="success.main">
                      This payment was successfully sent via Lob. You can update
                      associations, documents, reconciliation status, and
                      billing information, but the payment and payee details
                      cannot be changed.
                    </Typography>
                  )}
                  {hasRequeueableLobJob && (
                    <CheckboxField
                      name="requeueJob"
                      control={control}
                      label="Requeue Lob Job after updating this payment."
                    />
                  )}
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  minWidth: "1200px",
                  width: "100%",
                }}
              >
                {/* Main Form Content */}
                <Box sx={{ flex: 0.4, minWidth: "480px" }}>
                  <Stack spacing={3}>
                    <PayeeSection
                      control={control}
                      isLob={isLob}
                      notEditable={payeeNotEditable}
                    />

                    <PaymentSection
                      control={control}
                      isLob={isLob}
                      notEditable={paymentDetailsNotEditable}
                      billingMetadataEditable={metadataEditable}
                      setReportAmounts={setReportAmounts}
                      watch={watch}
                      setValue={setValue}
                    />

                    <InvoicesAttachmentsSection
                      control={control}
                      paymentId={paymentId}
                      existingPayment={existingPayment}
                      isLob={isLob}
                      notEditable={!metadataEditable}
                      tenantId={tenantId}
                      facilityId={facilityId}
                      reportId={reportId}
                      activityId={activityId}
                      fields={fields}
                      remove={remove}
                      watch={watch}
                      setValue={setValue}
                      hasUnsavedChanges={hasUnsavedReceiptChanges}
                      setAddEditDocumentDialogState={
                        setAddEditDocumentDialogState
                      }
                      deleteDocument={deleteDocument}
                    />
                  </Stack>
                </Box>

                {/* Right Panel with Vertical Divider */}
                <Box
                  sx={{
                    borderLeft: `2px solid ${theme.palette.divider}`,
                    pl: 3,
                    flex: 0.6,
                    minWidth: "720px",
                  }}
                >
                  <Stack spacing={3}>
                    {/* Associated Organization Section */}
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="h6">
                          Associated Organization
                        </Typography>
                        {isStaff && !paymentId && (
                          <Button
                            size="small"
                            onClick={() => setShowTenantPicker(true)}
                          >
                            Change
                          </Button>
                        )}
                      </Stack>
                      {/* Show validation error for tenant selection */}
                      {errors.tenantId && (
                        <ErrorDisplay error={errors.tenantId} />
                      )}
                      <Typography variant="body1">
                        {(() => {
                          // For existing payments, prioritize the payment's tenant information
                          if (
                            existingPayment &&
                            existingPayment?.tenant?.name
                          ) {
                            return existingPayment.tenant.name;
                          }

                          // If payment is still loading, show loading state
                          if (loadingPayment && paymentId) {
                            return "Loading...";
                          }

                          // Use selected tenant name if available
                          if (tenantName) {
                            return tenantName;
                          }

                          // For new payments or if payment tenant is not available, use current tenant
                          if (currentTenant?.name) {
                            return currentTenant.name;
                          }

                          // If we have a tenantId but no tenant name, show the ID as fallback
                          if (tenantId) {
                            return `Organization (ID: ${tenantId})`;
                          }

                          // Final fallback
                          return "Unknown Organization";
                        })()}
                      </Typography>
                    </Box>

                    {/* Associations Section - tighter spacing between Reports and Facilities */}
                    <Stack spacing={1.5}>
                      {(errors.associations as { message?: string } | undefined)
                        ?.message && (
                        <ErrorDisplay error={errors.associations as any} />
                      )}
                      <Box>
                        <AssociatedEntities
                          control={control}
                          tenantId={tenantId}
                          type="Report"
                          hideTitle={false}
                          titleOverride="Associated Reports"
                          disabled={!metadataEditable}
                          addDisabled={isAssociationTypeLocked("Report")}
                          addDisabledReason={getAssociationLockReason("Report")}
                          transactionFee={transactionFee}
                        />
                        {errors.associations && (
                          <ErrorDisplay
                            errors={errors}
                            fields={Object.keys(errors.associations || {})
                              .filter((index) => {
                                const association =
                                  associations?.[parseInt(index)];
                                return association?.reportId; // Only show errors for report associations
                              })
                              .map((index) => `associations.${index}.amount`)}
                          />
                        )}
                      </Box>

                      <Box>
                        <AssociatedEntities
                          control={control}
                          tenantId={tenantId}
                          type="Submission"
                          hideTitle={false}
                          titleOverride="Associated Submissions"
                          disabled={!metadataEditable}
                          addDisabled={isAssociationTypeLocked("Submission")}
                          addDisabledReason={getAssociationLockReason(
                            "Submission"
                          )}
                          transactionFee={transactionFee}
                        />
                        {errors.associations && (
                          <ErrorDisplay
                            errors={errors}
                            fields={Object.keys(errors.associations || {})
                              .filter((index) => {
                                const association =
                                  associations?.[parseInt(index)];
                                return association?.submissionId; // Only show errors for submission associations
                              })
                              .map((index) => `associations.${index}.amount`)}
                          />
                        )}
                      </Box>

                      <Box>
                        <AssociatedEntities
                          control={control}
                          tenantId={tenantId}
                          type="Facility"
                          hideTitle={false}
                          titleOverride="Associated Facilities"
                          disabled={!metadataEditable}
                          addDisabled={isAssociationTypeLocked("Facility")}
                          addDisabledReason={getAssociationLockReason(
                            "Facility"
                          )}
                          transactionFee={transactionFee}
                        />
                        {errors.associations && (
                          <ErrorDisplay
                            errors={errors}
                            fields={Object.keys(errors.associations || {})
                              .filter((index) => {
                                const association =
                                  associations?.[parseInt(index)];
                                return association?.facilityId; // Only show errors for facility associations
                              })
                              .map((index) => `associations.${index}.amount`)}
                          />
                        )}
                      </Box>
                    </Stack>

                    <ReconciliationSection
                      paymentId={paymentId}
                      existingPayment={existingPayment}
                      notEditable={!metadataEditable}
                      onShowReconcileDialog={() => setShowReconcileDialog(true)}
                    />

                    <CommentsSection
                      paymentId={paymentId}
                      existingPayment={existingPayment}
                      currentUserId={currentUser?.id}
                    />
                  </Stack>
                </Box>
              </Box>
            </Stack>
          )}
          <AddEditDocumentDialog
            dialogState={addEditDocumentDialogState}
            setDialogState={setAddEditDocumentDialogState}
            tenantId={tenantId}
            reportId={reportId}
            showAttestation
            disableDocumentTypeSelection={true} // Document type is predetermined by button clicked
            refetchQueries={[]} // Don't refetch to prevent losing form state
            onSubmit={(document) => {
              if (document.documentType === DocumentType.Receipt) {
                setValue("receiptDocument", document);
              } else if (document.documentType === DocumentType.Invoice) {
                // Add invoice for both new and existing payments
                appendInvoice({
                  id: v4(),
                  description: "Payment Invoice",
                  invoiceDocumentId: document.id,
                  invoiceDocument: {
                    id: document.id,
                    documentType: document.documentType,
                    title: document.title,
                    fileExtension: document.fileExtension,
                  },
                  invoiceNumber: "",
                });
              }

              // Only refetch if there are no other unsaved changes AND we have an existing payment
              if (!hasUnsavedReceiptChanges && paymentId) {
                refetchPayment();
              }
            }}
          />

          {/* Tenant Picker Dialog */}
          {showTenantPicker && (
            <TenantPickerDialog
              open={showTenantPicker}
              onClose={() => setShowTenantPicker(false)}
              onTenantSelected={(tenant) => {
                setValue("tenantId", tenant.id);
                setValue("associations", []);
                setShowTenantPicker(false);
              }}
            />
          )}

          {/* Reconciliation Dialog */}
          {showReconcileDialog && paymentId && existingPayment && (
            <ReconcilePaymentDialog
              open={showReconcileDialog}
              onClose={() => setShowReconcileDialog(false)}
              payments={[existingPayment as any]}
              onReconciled={() => {
                // Only refetch if there are no unsaved changes AND we have an existing payment
                if (!hasUnsavedReceiptChanges && paymentId) {
                  refetchPayment();
                }
              }}
            />
          )}

          {/* Unsaved changes confirmation dialog */}
          <ConfirmDialog
            open={showConfirmClose}
            onConfirm={handleConfirmedClose}
            onClose={() => setShowConfirmClose(false)}
            header={
              paymentId
                ? "You have unsaved document changes. Are you sure you want to close?"
                : "You have uploaded documents. Are you sure you want to close?"
            }
            info={(() => {
              if (!paymentId) {
                // New payment message
                const docCount =
                  fields.length > 0 && watch("receiptDocument")?.id
                    ? `${fields.length} invoice(s) and 1 receipt`
                    : fields.length > 0
                    ? `${fields.length} invoice(s)`
                    : "1 receipt";
                return `You have ${docCount} that will be lost if you close without creating the payment. These documents will be deleted.`;
              } else {
                // Existing payment message
                return "You have 1 pending receipt that will be lost if you close without saving. This document will be deleted.";
              }
            })()}
            confirmText={
              cleaningUpDocuments
                ? "Deleting Documents..."
                : "Yes, Close and Delete Documents"
            }
            loading={cleaningUpDocuments}
          />

          {/* Cancel Lob Check confirmation dialog */}
          <ConfirmDialog
            open={showCancelCheckConfirm}
            onConfirm={handleCancelLobCheck}
            onClose={() => setShowCancelCheckConfirm(false)}
            header="Cancel Lob Check?"
            info={`Are you sure you want to cancel this check payment${
              existingPayment?.amount ? ` for $${existingPayment.amount}` : ""
            }${
              existingPayment?.payee ? ` to ${existingPayment.payee}` : ""
            }? This action cannot be undone. The check will only be cancelled if it's still within Lob's cancellation window.`}
            confirmText={
              cancellingLobCheck ? "Cancelling..." : "Yes, Cancel Check"
            }
            loading={cancellingLobCheck}
          />
        </form>
      </Box>
    </Dialog>
  );
};

export { PaymentDialog };
